/**
 * Shared API client for all admin apps.
 * All data access goes through api.conan.school — no direct Supabase calls.
 * Includes debug logging for API calls (visible via Cmd+Shift+L).
 */

const API_BASE = import.meta.env.VITE_API_URL || '';
const SCHOOL_ID_KEY = 'fc_school_id';

// ==========================================
// API Call Logger (for DebugFooter)
// ==========================================
export interface ApiLogEntry {
    id: number;
    method: string;
    path: string;
    fullUrl: string;
    status: number | null;
    duration: number;
    timestamp: number;
    error: string | null;
    responseSize: number | null;
}

let _logIdCounter = 0;
const _apiLog: ApiLogEntry[] = [];
const _logListeners: Set<() => void> = new Set();
const MAX_LOG_ENTRIES = 100;

export function getApiLog(): ApiLogEntry[] {
    return _apiLog;
}

export function clearApiLog(): void {
    _apiLog.length = 0;
    _notifyListeners();
}

export function subscribeApiLog(listener: () => void): () => void {
    _logListeners.add(listener);
    return () => _logListeners.delete(listener);
}

function _notifyListeners() {
    _logListeners.forEach(fn => fn());
}

function _addLogEntry(entry: ApiLogEntry) {
    _apiLog.unshift(entry); // newest first
    if (_apiLog.length > MAX_LOG_ENTRIES) _apiLog.pop();
    _notifyListeners();
}

// ==========================================
// Cloudflare Access authenticates at the edge. The browser never handles an
// application bearer token; the admin router forwards the Access assertion.
// ==========================================
export function getAuthHeaders(includeJson = true): Record<string, string> {
    const schoolId = localStorage.getItem(SCHOOL_ID_KEY) || '';

    return {
        ...(includeJson ? { 'Content-Type': 'application/json' } : {}),
        ...(schoolId ? { 'x-school-id': schoolId } : {}),
    };
}

// ==========================================
// Core Fetch with Logging
// ==========================================
export async function apiFetch<T = unknown>(
    path: string,
    options?: RequestInit & { raw?: boolean }
): Promise<T> {
    const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
    const method = (options?.method || 'GET').toUpperCase();
    const start = performance.now();
    const logId = ++_logIdCounter;

    // Add pending entry
    const entry: ApiLogEntry = {
        id: logId,
        method,
        path,
        fullUrl: url,
        status: null,
        duration: 0,
        timestamp: Date.now(),
        error: null,
        responseSize: null,
    };

    try {
        const res = await fetch(url, {
            ...options,
            headers: {
                ...getAuthHeaders(!(options?.body instanceof FormData)),
                ...options?.headers,
            },
        });

        entry.status = res.status;
        entry.duration = Math.round(performance.now() - start);

        if (!res.ok) {
            const body = await res.text().catch(() => '');
            entry.error = body || res.statusText;
            entry.responseSize = body.length;
            _addLogEntry(entry);
            throw new Error(`API ${res.status}: ${body || res.statusText}`);
        }

        if (options?.raw) {
            _addLogEntry(entry);
            return res as unknown as T;
        }

        const json = await res.json();
        const jsonStr = JSON.stringify(json);
        entry.responseSize = jsonStr.length;
        _addLogEntry(entry);
        return json;
    } catch (err: any) {
        entry.duration = Math.round(performance.now() - start);
        if (!entry.error) entry.error = err.message;
        if (!entry.status) entry.status = 0;
        _addLogEntry(entry);
        throw err;
    }
}

/** Convenience wrappers */
export const api = {
    get: <T = unknown>(path: string) => apiFetch<T>(path),

    post: <T = unknown>(path: string, body: unknown) =>
        apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) }),

    put: <T = unknown>(path: string, body: unknown) =>
        apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),

    patch: <T = unknown>(path: string, body: unknown) =>
        apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),

    del: <T = unknown>(path: string) =>
        apiFetch<T>(path, { method: 'DELETE' }),

    upload: <T = unknown>(path: string, body: FormData) =>
        apiFetch<T>(path, { method: 'POST', body }),
};
